import React from "react";
import { LoginErrorCode, verifyMFA } from "../../../api/auth";
import AppButton from "../../../components/AppButton";
import AuthFlowTitle from "./AuthFlowTitle";
import OTPInput from "./OTPInput";
import ErrorMessage from "./ErrorMessage";
import { twMerge } from "tailwind-merge";

type Props = {
    username: string;
    sendCodeAgain?: () => Promise<void>;
    onSuccess?: () => Promise<void> | void;
    onError?: (errorCode?: LoginErrorCode) => void;
    title?: string;
    submitButtonText?: string;
    codeSentVia: "email" | "sms";
};

function MFAForm({
    username,
    sendCodeAgain,
    title,
    submitButtonText,
    onSuccess,
    onError,
    codeSentVia,
}: Props) {
    const [otp, setOtp] = React.useState<string[]>(Array(6).fill(""));
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState("");
    const [countdown, setCountdown] = React.useState(0);
    const [countdownEndTime, setCountdownEndTime] = React.useState<
        number | null
    >(null);
    const [isResending, setIsResending] = React.useState(false);

    React.useEffect(() => {
        let timer: NodeJS.Timeout;
        if (countdownEndTime) {
            const updateCountdown = () => {
                const now = Date.now();
                const remainingTime = Math.max(
                    0,
                    Math.ceil((countdownEndTime - now) / 1000)
                );
                setCountdown(remainingTime);

                if (remainingTime > 0) {
                    timer = setTimeout(updateCountdown, 1000);
                } else {
                    setCountdownEndTime(null);
                }
            };
            updateCountdown();
        } else {
            setCountdown(0);
        }

        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [countdownEndTime]);

    const handleSendCodeAgain = async () => {
        if (!sendCodeAgain || countdown > 0) return;

        setIsResending(true);
        setError("");
        try {
            await sendCodeAgain();
            const endTime = Date.now() + 60 * 1000; // 60 seconds from now
            setCountdownEndTime(endTime);
        } catch (error) {
            setError(
                error instanceof Error ? error.message : "Failed to send code"
            );
        } finally {
            setIsResending(false);
        }
    };

    const submit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (otp.filter(Boolean).length < 6) return null;

        setSubmitting(true);
        setError("");
        // let errorMsg = "Wrong OTP code";
        try {
            const code = otp.join("");
            const res = await verifyMFA(username, code);

            if (res.error_code || res.error) {
                if (onError) onError(res.error_code);
                if (res.error_code === "incorrect-code") {
                    throw new Error("Wrong OTP code");
                } else if (res.error_code === "invalid-credentials") {
                    throw new Error("Invalid credentials");
                } else if (res.error_code === "password-previously-used") {
                    throw new Error("Password has previously been used.");
                } else if (res.error) {
                    throw new Error(res.error);
                }
            }

            if (onSuccess) {
                await onSuccess();
            }
        } catch (error) {
            setError(
                error instanceof Error
                    ? error.message
                    : "Unknown error occurred"
            );
        } finally {
            setSubmitting(false);
        }
    };

    const sendAnotherCodeIsDisabled = countdown > 0 || isResending;

    return (
        <form className="h-full flex flex-col gap-6" onSubmit={submit}>
            <AuthFlowTitle>{title || "Welcome to CPETWise"}</AuthFlowTitle>
            <p className="mb-10">
                Please enter 6-digit verification code sent via{" "}
                {codeSentVia === "email" ? "Email" : "SMS"}.
            </p>
            <OTPInput className="" otp={otp} setOtp={setOtp} />
            {sendCodeAgain ? (
                <button
                    type="button"
                    className={twMerge(
                        `text-left text-gray-medium`,
                        sendAnotherCodeIsDisabled
                            ? "opacity-50 cursor-not-allowed"
                            : "underline hover:no-underline"
                    )}
                    onClick={handleSendCodeAgain}
                    disabled={sendAnotherCodeIsDisabled}
                >
                    {isResending
                        ? "Sending code..."
                        : countdown > 0
                        ? `Send me another code (${countdown}s)`
                        : "Don't work? Send me another code."}
                </button>
            ) : null}
            <div className="mt-auto w-full flex flex-col">
                <ErrorMessage message={error} className="mb-0.5" />
                <AppButton
                    className=""
                    type="submit"
                    disabled={otp.some((el) => el.length === 0)}
                    loading={submitting}
                >
                    {submitButtonText || "Login"}
                </AppButton>
            </div>
        </form>
    );
}

export default MFAForm;
